import { Zap } from "lucide-react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import type { RecipientTelemetrySummary, Scope, TelemetryOutlier } from "@busright/data-contract";

import { DataTable } from "@/components/DataTable";
import { Drillable } from "@/components/Drillable";
import { MetricLabel } from "@/components/MetricTooltip";
import { formatMb } from "@/lib/format";

// =================================================================================================
// TelBatteryVoltageInsight — the surface's lead story, not a fleet dump. Combines two real signals:
// (1) the lowest-voltage device outliers (real per-device latest readings — link to Device-360), and
// (2) when a district is selected, that district's charging-underperforming flag (from the
// recipient-scoped bundle's avgBatteryVoltageV, exactly the John-flagged "battery voltage → district
// with underperforming charging hardware" signal). At fleet scope there's no per-outlier district
// key, so the fleet view leads with the device tail and tells the reader to pick a district via the
// scope switcher to see the per-district charging read.
// =================================================================================================

const VOLT_DIGITS = 3;

export interface TelBatteryVoltageInsightProps {
  readonly scope: Scope;
  // Fleet-wide lowest-voltage device outliers (always available).
  readonly lowestVoltageOutliers: readonly TelemetryOutlier[];
  // Present only when scope is narrower than fleet (district/account) via the recipient bundle.
  readonly scopedTelemetry: RecipientTelemetrySummary | null;
}

export const TelBatteryVoltageInsight = ({
  scope,
  lowestVoltageOutliers,
  scopedTelemetry,
}: TelBatteryVoltageInsightProps): ReactNode => {
  const navigate = useNavigate();
  const isScoped = scope.level !== "fleet";

  return (
    <div>
      {isScoped && scopedTelemetry ? (
        <div className="tel-voltage-scoped">
          <Drillable metricKey="battery.voltageV" scope={scope} label="Drill into battery voltage">
            <div className="panel-stat">
              <MetricLabel metricKey="battery.voltageV" label="Avg battery voltage" />
              <span className="panel-stat-value br-tabular-nums">
                {scopedTelemetry.avgBatteryVoltageV === null
                  ? "—"
                  : `${scopedTelemetry.avgBatteryVoltageV.toFixed(VOLT_DIGITS)} V`}
              </span>
            </div>
          </Drillable>
          <Drillable metricKey="memory.usedMb" scope={scope} label="Drill into memory used">
            <div className="panel-stat">
              <MetricLabel metricKey="memory.usedMb" label="Avg memory used" />
              <span className="panel-stat-value br-tabular-nums">
                {scopedTelemetry.avgMemoryUsedMb === null
                  ? "—"
                  : `${formatMb(scopedTelemetry.avgMemoryUsedMb)} / ${
                      scopedTelemetry.avgMemoryTotalMb === null
                        ? "—"
                        : formatMb(scopedTelemetry.avgMemoryTotalMb)
                    }`}
              </span>
            </div>
          </Drillable>
          {scopedTelemetry.chargingUnderperforming ? (
            <p className="panel-flag">
              <Zap size={13} aria-hidden /> Charging voltage is below the healthy band for this
              scope — worth a replacement/charging-hardware investigation.
            </p>
          ) : (
            <p className="panel-subnote">Charging voltage is within the healthy band.</p>
          )}
        </div>
      ) : (
        <p className="card-subtitle" style={{ marginBottom: 10 }}>
          Fleet-wide view — pick a district in the scope switcher to see whether ITS charging
          hardware is underperforming (avg voltage vs the healthy band).
        </p>
      )}

      <DataTable
        rows={lowestVoltageOutliers}
        rowKey={(r) => r.esperDeviceId}
        emptyTitle="No voltage outliers"
        onRowClick={(r) => {
          if (r.serial) {
            void navigate(`/device/${r.serial}`);
          }
        }}
        columns={[
          {
            key: "serial",
            header: "Device",
            render: (r) => <span className="mono">{r.serial ?? "—"}</span>,
          },
          { key: "hwModel", header: "Model", render: (r) => r.hwModel ?? "—" },
          {
            key: "value",
            header: "Voltage",
            align: "right",
            render: (r) => <span className="br-tabular-nums">{r.value.toFixed(VOLT_DIGITS)}V</span>,
          },
        ]}
      />
    </div>
  );
};
